import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { addDays, subDays, format, startOfWeek, startOfMonth } from 'date-fns';
import { isMobile } from 'mewe/shared/utils';

export default class AppAmbassador extends Component {
  @service settings;

  @tracked currentEndDate = new Date();
  @tracked selectedInterval = '7D';
  @tracked chartType = 'line';
  @tracked isLineChart = true;

  get isMobile() {
    return isMobile();
  }

  get theme() {
    if (this.settings.isDarkThemeEnabled) {
      return {
        colors: ['#B8D500', '#50A9CE', '#F27B9B', '#AB82C4', '#7BC07B'],
        chart: {
          backgroundColor: '#303030',
          plotBackgroundColor: '#303030',
        },
        title: {
          style: {
            color: '#f7f7f7',
          },
        },
        xAxis: {
          gridLineColor: '#404040',
          lineColor: '#404040',
          tickColor: '#404040',
          labels: {
            style: {
              color: '#f7f7f7',
            },
          },
          title: {
            style: {
              color: '#f7f7f7',
            },
          },
        },
        yAxis: {
          gridLineColor: '#404040',
          lineColor: '#404040',
          tickColor: '#404040',
          labels: {
            style: {
              color: '#f7f7f7',
            },
          },
          title: {
            style: {
              color: '#f7f7f7',
            },
          },
        },
        legend: {
          itemStyle: {
            color: '#f7f7f7',
          },
        },
        tooltip: {
          backgroundColor: '#212121',
          borderColor: '#404040',
          style: {
            color: '#f7f7f7',
          },
        },
      };
    } else {
      return {
        colors: ['#2f7ed8', '#9370DB', '#8bbc21', '#ff8c00'],
      };
    }
  }

  get intervalDays() {
    switch (this.selectedInterval) {
      case '30D':
        return 30;
      case '90D':
        return 90;
      case '12M':
        return 365;
      default:
        return 7;
    }
  }

  get endDateFormatted() {
    return format(this.currentEndDate, 'MM/dd/yyyy');
  }

  get startDateFormatted() {
    return format(this.startDate, 'MM/dd/yyyy');
  }

  get startDate() {
    return subDays(this.currentEndDate, this.intervalDays - 1);
  }

  get stats() {
    const rawStats = this.args.ambassador.contract.stats.filter((stat) => {
      const statDate = new Date(stat.time);
      return statDate >= this.startDate && statDate <= this.currentEndDate;
    });

    // Aggregate data based on interval
    if (this.selectedInterval === '90D') {
      return this.aggregateStatsByWeek(rawStats);
    } else if (this.selectedInterval === '12M') {
      return this.aggregateStatsByMonth(rawStats);
    }

    return rawStats;
  }

  aggregateStatsByWeek(stats) {
    const weekMap = new Map();

    stats.forEach((stat) => {
      const date = new Date(stat.time);
      const weekStart = startOfWeek(date);
      const weekKey = weekStart.toISOString();

      if (!weekMap.has(weekKey)) {
        weekMap.set(weekKey, {
          time: weekStart.toISOString(),
          followers: 0,
          invitedFromLink: 0,
          emojis: 0,
          comments: 0,
          promoCodes: [],
        });
      }

      const weekStat = weekMap.get(weekKey);
      weekStat.followers += stat.followers;
      weekStat.invitedFromLink += stat.invitedFromLink;
      weekStat.emojis += stat.emojis;
      weekStat.comments += stat.comments;
    });

    return Array.from(weekMap.values());
  }

  aggregateStatsByMonth(stats) {
    const monthMap = new Map();

    stats.forEach((stat) => {
      const date = new Date(stat.time);
      const monthStart = startOfMonth(date);
      const monthKey = monthStart.toISOString();

      if (!monthMap.has(monthKey)) {
        monthMap.set(monthKey, {
          time: monthStart.toISOString(),
          followers: 0,
          invitedFromLink: 0,
          emojis: 0,
          comments: 0,
          promoCodes: [],
        });
      }

      const monthStat = monthMap.get(monthKey);
      monthStat.followers += stat.followers;
      monthStat.invitedFromLink += stat.invitedFromLink;
      monthStat.emojis += stat.emojis;
      monthStat.comments += stat.comments;
    });

    return Array.from(monthMap.values());
  }

  get isCurrentPeriod() {
    return format(this.currentEndDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
  }

  get baseChartConfig() {
    const getDateFormat = () => {
      switch (this.selectedInterval) {
        case '7D':
        case '30D':
          return '%b %e'; // Shows: "Jan 1"
        case '90D':
          return 'Week %U'; // Using %U for week number
        case '12M':
          return '%b %Y'; // Shows: "Jan 2024"
        default:
          return '%b %e';
      }
    };

    const pointInterval = this.getPointInterval();
    const selectedInterval = this.selectedInterval;

    return {
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          day: getDateFormat(),
          week: getDateFormat(),
          month: getDateFormat(),
          year: getDateFormat(),
        },
        title: {
          text: __('Date'),
        },
        min: this.startDate.getTime(),
        max: this.currentEndDate.getTime(),
        labels: {
          rotation: this.intervalDays > 7 ? -45 : 0,
          formatter: function () {
            if (selectedInterval === '90D') {
              const date = new Date(this.value);
              const weekNumber = Math.ceil(
                (date.getTime() - new Date(date.getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000),
              );
              return `Week ${weekNumber}`;
            }
            return this.axis.defaultLabelFormatter.call(this);
          },
          style: {
            fontSize: '11px',
          },
        },
      },
      yAxis: {
        title: {
          text: __('Count'),
        },
        min: 0,
      },
      tooltip: {
        shared: true,
        crosshairs: true,
        xDateFormat:
          this.selectedInterval === '90D' ? 'Week %U' : this.selectedInterval === '12M' ? '%B %Y' : '%Y-%m-%d',
      },
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
      },
      plotOptions: {
        series: {
          pointInterval: pointInterval,
          pointStart: this.startDate.getTime(),
        },
      },
    };
  }

  getPointInterval() {
    switch (this.selectedInterval) {
      case '7D':
        return 24 * 3600 * 1000; // 1 day
      case '30D':
        return 24 * 3600 * 1000; // 1 day
      case '90D':
        return 7 * 24 * 3600 * 1000; // 1 week
      case '12M':
        return 30 * 24 * 3600 * 1000; // ~1 month
      default:
        return 24 * 3600 * 1000;
    }
  }

  get lineChartConfig() {
    const baseConfig = {
      ...this.baseChartConfig,
      chart: {
        type: 'line',
        zoomType: 'x',
        height: 500,
        animation: {
          duration: 500,
        },
        ...this.theme.chart,
      },
      title: {
        text: `${__('Ambassador Activity Trends')} (${this.selectedInterval})`,
        ...this.theme.title,
      },
      plotOptions: {
        ...this.baseChartConfig.plotOptions,
        line: {
          marker: {
            enabled: true,
            radius: this.intervalDays <= 7 ? 4 : 2,
          },
        },
      },
    };

    // Merge theme settings for axes
    if (this.theme.xAxis) {
      baseConfig.xAxis = { ...baseConfig.xAxis, ...this.theme.xAxis };
    }
    if (this.theme.yAxis) {
      baseConfig.yAxis = { ...baseConfig.yAxis, ...this.theme.yAxis };
    }
    if (this.theme.legend) {
      baseConfig.legend = { ...baseConfig.legend, ...this.theme.legend };
    }
    if (this.theme.tooltip) {
      baseConfig.tooltip = { ...baseConfig.tooltip, ...this.theme.tooltip };
    }

    return baseConfig;
  }

  get barChartConfig() {
    const baseConfig = {
      ...this.baseChartConfig,
      chart: {
        type: 'column',
        height: 500,
        animation: {
          duration: 500,
        },
        ...this.theme.chart,
      },
      title: {
        text: `${__('Ambassador Activity Distribution')} (${this.selectedInterval})`,
        ...this.theme.title,
      },
      plotOptions: {
        ...this.baseChartConfig.plotOptions,
        column: {
          pointPadding: 0.2,
          borderWidth: 0,
          groupPadding: 0.1,
        },
      },
    };

    // Merge theme settings for axes
    if (this.theme.xAxis) {
      baseConfig.xAxis = { ...baseConfig.xAxis, ...this.theme.xAxis };
    }
    if (this.theme.yAxis) {
      baseConfig.yAxis = { ...baseConfig.yAxis, ...this.theme.yAxis };
    }
    if (this.theme.legend) {
      baseConfig.legend = { ...baseConfig.legend, ...this.theme.legend };
    }
    if (this.theme.tooltip) {
      baseConfig.tooltip = { ...baseConfig.tooltip, ...this.theme.tooltip };
    }

    return baseConfig;
  }

  get donutChartConfig() {
    return {
      chart: {
        type: 'pie',
        height: 500,
        animation: {
          duration: 500,
        },
        ...this.theme.chart,
      },
      title: {
        text: `${__('Ambassador Activity Distribution')} (${this.selectedInterval})`,
        ...this.theme.title,
      },
      plotOptions: {
        pie: {
          innerSize: '50%',
          depth: 45,
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b>: {point.percentage:.1f} %',
          },
        },
      },
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        ...this.theme.legend,
      },
      tooltip: {
        ...this.theme.tooltip,
      },
    };
  }

  get donutChartOptions() {
    const stats = this.stats;

    // Sum up all stats in the interval
    const totalStats = stats.reduce(
      (acc, stat) => {
        return {
          followers: acc.followers + (stat.followers ?? 0),
          invitedFromLink: acc.invitedFromLink + (stat.invitedFromLink ?? 0),
          emojis: acc.emojis + (stat.emojis ?? 0),
          comments: acc.comments + (stat.comments ?? 0),
        };
      },
      {
        followers: 0,
        invitedFromLink: 0,
        emojis: 0,
        comments: 0,
      },
    );

    return [
      {
        name: __('Activity Distribution'),
        data: [
          [__('Followers'), totalStats?.followers ?? 0],
          [__('Invited Users'), totalStats?.invitedFromLink ?? 0],
          [__('Emojis'), totalStats?.emojis ?? 0],
          [__('Comments'), totalStats?.comments ?? 0],
        ],
      },
    ];
  }

  get seriesData() {
    const stats = this.stats;
    return [
      {
        name: __('Followers'),
        data: stats.map((stat) => [new Date(stat.time).getTime(), stat.followers]),
      },
      {
        name: __('Invited Users'),
        data: stats.map((stat) => [new Date(stat.time).getTime(), stat.invitedFromLink]),
      },
      {
        name: __('Emojis'),
        data: stats.map((stat) => [new Date(stat.time).getTime(), stat.emojis]),
      },
      {
        name: __('Comments'),
        data: stats.map((stat) => [new Date(stat.time).getTime(), stat.comments]),
      },
    ];
  }

  get lineChartOptions() {
    return this.seriesData;
  }

  get barChartOptions() {
    return this.seriesData;
  }

  @action
  setInterval(interval) {
    this.selectedInterval = interval;
    this.currentEndDate = new Date(); // Reset to current date when changing interval
  }

  @action
  previousPeriod() {
    this.currentEndDate = subDays(this.currentEndDate, this.intervalDays);
  }

  @action
  nextPeriod() {
    const newEndDate = addDays(this.currentEndDate, this.intervalDays);
    if (newEndDate <= new Date()) {
      this.currentEndDate = newEndDate;
    }
  }

  @action
  toggleChartType() {
    this.isLineChart = !this.isLineChart;
  }

  @action
  setChartType(type) {
    this.chartType = type;
  }
}
